import { useParams } from "react-router-dom";
import { useEffect, useState, FormEvent } from "react";
import {
  fetchPublicRegistrationForm,
  submitPublicRegistration,
  PublicRegistrationMeta,
  PublicRegistrationResult,
} from "../services/customerRegistration";
import { Button } from "../components/ui/Button";
import { Loader2, CheckCircle2, ExternalLink } from "lucide-react";

export default function PublicCustomerRegistrationPage() {
  const { encodedId } = useParams<{ encodedId: string }>();
  const [meta, setMeta] = useState<PublicRegistrationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PublicRegistrationResult | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [callingCode, setCallingCode] = useState("+44");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [address, setAddress] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  useEffect(() => {
    if (!encodedId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPublicRegistrationForm(encodedId);
        setMeta(data);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load registration form"
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [encodedId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!encodedId) return;

    setSubmitting(true);
    setFieldErrors({});
    setError(null);

    const pin = pinCode.trim();
    const errors: Record<string, string> = {};
    if (!pin) {
      errors.pin_code = "PIN is required";
    } else if (!/^\d{4}$/.test(pin)) {
      errors.pin_code = "PIN must be exactly 4 digits";
    }
    if (!ageConfirmed) {
      errors.age_confirmed =
        "Please confirm that you are 18 years of age or older";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      const data = await submitPublicRegistration(encodedId, {
        name: name.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        calling_code: callingCode.trim() || "+44",
        date_of_birth: dateOfBirth || undefined,
        address: address.trim() || undefined,
        pin_code: pin,
        age_confirmed: true,
      });
      setResult(data);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        const mapped: Record<string, string> = {};
        Object.keys(errors).forEach((key) => {
          const val = errors[key];
          mapped[key] = Array.isArray(val) ? val[0] : String(val);
        });
        setFieldErrors(mapped);
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Registration failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="h-10 w-10 animate-spin text-[#885ABB]" />
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-6">
          <div className="text-center space-y-3">
            <CheckCircle2 className="mx-auto h-14 w-14 text-green-500" />
            <h1 className="text-2xl font-bold text-[#402B69]">You’re registered</h1>
            <p className="text-gray-600 text-sm">
              Thanks {result.customer.name}. Your details have been saved
              {meta?.company?.name ? ` with ${meta.company.name}` : ""}.
            </p>
            {result.forms_sent.length > 0 ? (
              <p className="text-sm text-[#885ABB] font-medium">
                Check your email/SMS for consultation form
                {result.forms_sent.length > 1 ? "s" : ""}. You can also open them
                below.
              </p>
            ) : null}
          </div>

          {result.forms_sent.length > 0 && (
            <div className="space-y-2">
              {result.forms_sent.map((form) => (
                <a
                  key={form.url}
                  href={form.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 rounded-xl border border-purple-100 bg-purple-50/50 px-4 py-3 text-sm font-medium text-[#402B69] hover:bg-purple-50 transition-colors"
                >
                  <span>{form.title || "Consultation form"}</span>
                  <ExternalLink size={16} className="text-[#885ABB] shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4 py-10">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-[#402B69] px-8 py-6 text-white">
          <p className="text-xs uppercase tracking-wider text-purple-200 font-semibold">
            Customer registration
          </p>
          <h1 className="text-xl font-bold mt-1">{meta?.company?.name || "Register"}</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-4">
          {meta?.will_send_forms ? (
            <p className="text-xs text-gray-500 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
              After you register, {meta.linked_forms_count} consultation form
              {meta.linked_forms_count === 1 ? "" : "s"} will be sent to your
              email/SMS.
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          ) : null}

          <Field
            label="Full name"
            required
            error={fieldErrors.name}
            value={name}
            onChange={setName}
            autoComplete="name"
          />
          <Field
            label="Email"
            type="email"
            required
            error={fieldErrors.email}
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Code"
              required={false}
              error={fieldErrors.calling_code}
              value={callingCode}
              onChange={setCallingCode}
              className="col-span-1"
            />
            <Field
              label="Mobile"
              type="tel"
              required
              error={fieldErrors.mobile}
              value={mobile}
              onChange={setMobile}
              autoComplete="tel"
              className="col-span-2"
            />
          </div>
          <Field
            label="Date of birth"
            type="date"
            required={false}
            error={fieldErrors.date_of_birth}
            value={dateOfBirth}
            onChange={setDateOfBirth}
          />
          <Field
            label="Address"
            required={false}
            error={fieldErrors.address}
            value={address}
            onChange={setAddress}
            autoComplete="street-address"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              PIN <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pinCode}
                required
                autoComplete="new-password"
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setPinCode(next);
                  if (fieldErrors.pin_code) {
                    setFieldErrors((prev) => {
                      const { pin_code: _, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="4-digit PIN"
                className={`w-full h-11 rounded-xl border px-3 pr-16 text-sm outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB] ${
                  fieldErrors.pin_code ? "border-red-300" : "border-gray-200"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-semibold text-[#885ABB] hover:bg-purple-50 rounded-lg"
              >
                {showPin ? "Hide" : "Show"}
              </button>
            </div>
            {fieldErrors.pin_code ? (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.pin_code}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Enter a 4-digit PIN for kiosk / account access at the salon.
              </p>
            )}
          </div>

          <div
            className={`rounded-xl border px-3 py-3 ${
              fieldErrors.age_confirmed
                ? "border-red-300 bg-red-50/40"
                : "border-gray-200 bg-gray-50/60"
            }`}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => {
                  setAgeConfirmed(e.target.checked);
                  if (fieldErrors.age_confirmed) {
                    setFieldErrors((prev) => {
                      const { age_confirmed: _, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
              />
              <span className="text-sm text-gray-700 leading-snug">
                I confirm that I am{" "}
                <span className="font-semibold">18 years of age or older</span>
              </span>
            </label>
            {fieldErrors.age_confirmed ? (
              <p className="mt-2 text-xs text-red-600">
                {fieldErrors.age_confirmed}
              </p>
            ) : null}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-[#885ABB] hover:bg-[#7a4dad] text-white font-semibold rounded-xl"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Registering…
              </span>
            ) : (
              "Register"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  error,
  autoComplete,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-11 rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB] ${
          error ? "border-red-300" : "border-gray-200"
        }`}
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
